// client/src/components/pages/LexiStar/Drivers/DriverDetail.jsx
//
// Driver profile drawer (LS-09 §5.9 / §5.14 driver tabs; FR-DRV-001/003/005,
// FR-NTF-011): who the driver is and how to reach them; consent per channel
// with its evidence and a form to record a new one; the allocation timeline
// (a fortnight around today) with the editor; the last messages to and from
// the driver, linking to the full conversation.

import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FaTimes, FaEdit, FaPlus, FaComments } from 'react-icons/fa';
import ThemedSelect from '../../../common/ThemedSelect';
import { LoadingState, inputClasses } from '../StatusBoard/atoms';
import { fmtDateTime } from '../StatusBoard/format';
import { formatMiningDateShort } from '../../../../utils/dateUtils';
import { StateChip, ChannelBadge } from '../Messages/MessageAtoms';
import { intentLabel } from '../Messages/messageMeta';
import { getDriver, recordConsent, closeAllocation } from './driversApi';
import { errorText } from './DriverDialog';
import AllocationEditor from './AllocationEditor';
import {
  DRIVER_CHANNELS,
  CONSENT_METHODS,
  CONSENT_META,
  channelLabel,
  languageLabel,
  reachableChannels,
  defaultConsentWording,
  timelineBars,
  laneBars,
  isCurrent,
  spanOf,
} from './driverLogic';

const DAY = 86400000;

function Section({ title, action, children }) {
  return (
    <section className="px-6 py-4 border-b border-slate-100" aria-label={title}>
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{title}</h3>
        {action}
      </div>
      {children}
    </section>
  );
}

function ConsentChip({ state }) {
  const meta = CONSENT_META[state] || CONSENT_META.none;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold ${meta.className}`}
    >
      <span aria-hidden="true">{meta.glyph}</span>
      {meta.label}
    </span>
  );
}

function ConsentForm({ driver, onSaved, onCancel }) {
  const [channel, setChannel] = useState('whatsapp');
  const [state, setState] = useState('opted_in');
  const [method, setMethod] = useState('form');
  const [wording, setWording] = useState(() => defaultConsentWording('whatsapp', driver.language));
  const [touched, setTouched] = useState(false);
  const [note, setNote] = useState('');
  const [reference, setReference] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!touched)
      setWording(state === 'opted_in' ? defaultConsentWording(channel, driver.language) : '');
  }, [channel, state, driver.language, touched]);

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await recordConsent(driver.id, {
        channel,
        state,
        method,
        wording: wording.trim() || null,
        evidence: {
          ...(note.trim() ? { note: note.trim() } : {}),
          ...(reference.trim() ? { reference: reference.trim() } : {}),
        },
      });
      onSaved();
    } catch (err) {
      setError(errorText(err, 'The consent was not recorded'));
    } finally {
      setSaving(false);
    }
  };

  const needsWording = state === 'opted_in' && !wording.trim();

  return (
    <div
      className="mt-3 rounded-lg border border-slate-200 bg-slate-50 p-4 space-y-3"
      role="group"
      aria-label="Record consent"
    >
      {error && (
        <p
          role="alert"
          className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2"
        >
          {error}
        </p>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div>
          <label
            htmlFor="consent-channel"
            className="block text-xs font-medium text-slate-600 mb-1"
          >
            Channel
          </label>
          <ThemedSelect
            id="consent-channel"
            value={channel}
            onChange={(e) => setChannel(e.target.value)}
            className={inputClasses}
          >
            {DRIVER_CHANNELS.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </ThemedSelect>
        </div>
        <div>
          <label htmlFor="consent-state" className="block text-xs font-medium text-slate-600 mb-1">
            Consent
          </label>
          <ThemedSelect
            id="consent-state"
            value={state}
            onChange={(e) => setState(e.target.value)}
            className={inputClasses}
          >
            <option value="opted_in">Opted in</option>
            <option value="opted_out">Opted out</option>
          </ThemedSelect>
        </div>
        <div>
          <label htmlFor="consent-method" className="block text-xs font-medium text-slate-600 mb-1">
            How it was given
          </label>
          <ThemedSelect
            id="consent-method"
            value={method}
            onChange={(e) => setMethod(e.target.value)}
            className={inputClasses}
          >
            {CONSENT_METHODS.map((m) => (
              <option key={m.value} value={m.value}>
                {m.label}
              </option>
            ))}
          </ThemedSelect>
        </div>
      </div>
      <div>
        <label htmlFor="consent-wording" className="block text-xs font-medium text-slate-600 mb-1">
          Wording the driver agreed to{state === 'opted_in' ? '' : ' (optional)'}
        </label>
        <textarea
          id="consent-wording"
          value={wording}
          onChange={(e) => {
            setTouched(true);
            setWording(e.target.value);
          }}
          rows={2}
          className={inputClasses}
        />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label
            htmlFor="consent-reference"
            className="block text-xs font-medium text-slate-600 mb-1"
          >
            Evidence reference
          </label>
          <input
            id="consent-reference"
            value={reference}
            onChange={(e) => setReference(e.target.value)}
            placeholder="Form number, file name…"
            className={inputClasses}
          />
        </div>
        <div>
          <label htmlFor="consent-note" className="block text-xs font-medium text-slate-600 mb-1">
            Note
          </label>
          <input
            id="consent-note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            className={inputClasses}
          />
        </div>
      </div>
      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="px-3 py-1.5 text-sm font-medium text-slate-600 bg-surface border border-slate-200 rounded-lg hover:bg-slate-50"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={save}
          disabled={saving || needsWording}
          className="px-4 py-1.5 text-sm font-semibold text-white rounded-lg bg-primary-500 hover:bg-primary-600 disabled:bg-slate-400 disabled:cursor-not-allowed"
        >
          {saving ? 'Saving...' : 'Record consent'}
        </button>
      </div>
    </div>
  );
}

const LANE_PX = 22;

function AllocationTimeline({ allocations, now }) {
  const start = now - 7 * DAY;
  const end = now + 7 * DAY;
  const bars = laneBars(timelineBars(allocations, start, end));
  const lanes = Math.max(1, ...bars.map((b) => b.lane + 1));
  const days = Array.from({ length: 15 }, (_, i) => start + i * DAY);
  return (
    <div aria-label="Allocation timeline" role="img" aria-roledescription="timeline">
      <div
        className="relative rounded-md bg-slate-100 overflow-hidden"
        style={{ height: lanes * LANE_PX + 12 }}
      >
        {days.map((d, i) => (
          <span
            key={d}
            className="absolute top-0 bottom-0 border-l border-slate-200"
            style={{ left: `${(i / 14) * 100}%` }}
          />
        ))}
        {bars.map((b) => (
          <span
            key={b.allocation.id}
            className={`absolute rounded text-[10px] font-semibold px-1.5 flex items-center overflow-hidden whitespace-nowrap ${b.overlap ? 'bg-surface text-red-800 ring-2 ring-inset ring-red-500' : 'bg-slate-700 text-slate-50'}`}
            style={{
              left: `${b.left}%`,
              width: `${b.width}%`,
              top: 6 + b.lane * LANE_PX,
              height: LANE_PX - 4,
            }}
            title={`${b.allocation.registration || 'Truck'}: ${fmtDateTime(b.allocation.from)} → ${b.allocation.to ? fmtDateTime(b.allocation.to) : 'open-ended'}${b.overlap ? ' — overlaps another truck' : ''}`}
          >
            {b.overlap && <span aria-hidden="true">!&nbsp;</span>}
            {b.allocation.registration}
          </span>
        ))}
        <span
          className="absolute top-0 bottom-0 w-0.5 bg-primary-600"
          style={{ left: '50%' }}
          title="Now"
        />
      </div>
      <div className="flex justify-between text-[10px] text-slate-500 mt-1 tabular-nums">
        <span>{formatMiningDateShort(new Date(start).toISOString())}</span>
        <span>today</span>
        <span>{formatMiningDateShort(new Date(end).toISOString())}</span>
      </div>
    </div>
  );
}

export default function DriverDetail({ driverId, canManage, assets, onClose, onEdit, onChanged }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [consentOpen, setConsentOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [allocOpen, setAllocOpen] = useState(false);
  const [busy, setBusy] = useState(null);
  const [now, setNow] = useState(() => Date.now());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await getDriver(driverId));
      setNow(Date.now());
      setError(null);
    } catch (err) {
      setError(errorText(err, 'Failed to load the driver'));
    } finally {
      setLoading(false);
    }
  }, [driverId]);

  useEffect(() => {
    load();
  }, [load]);

  const refresh = () => {
    load();
    onChanged?.();
  };

  const endAllocation = async (a) => {
    setBusy(a.id);
    try {
      await closeAllocation(a.id);
      refresh();
    } catch (err) {
      setError(errorText(err, 'Could not end the allocation'));
    } finally {
      setBusy(null);
    }
  };

  const driver = data?.driver;
  const current = data?.consents?.current || driver?.consents || {};
  const currentMap = Array.isArray(current)
    ? Object.fromEntries(current.map((c) => [c.channel, c]))
    : current;
  const history = data?.consents?.history || [];
  const allocations = data?.allocations || [];
  const messages = data?.messages || [];
  const reach = driver ? reachableChannels({ ...driver, consents: currentMap }) : [];

  return (
    <div
      className="fixed inset-0 z-40 flex justify-end bg-black/40"
      onClick={onClose}
      role="presentation"
    >
      <aside
        role="dialog"
        aria-modal="true"
        aria-label={driver ? `Driver ${driver.name}` : 'Driver'}
        className="bg-surface w-full max-w-2xl h-full overflow-y-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 z-10 bg-surface px-6 py-4 border-b border-slate-200 flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h2 className="text-lg font-bold text-slate-900 truncate">
              {driver?.name || 'Driver'}
            </h2>
            {driver && (
              <p className="text-sm text-slate-500">
                {driver.tag ? `${driver.tag} · ` : ''}
                {languageLabel(driver.language)} ·{' '}
                {driver.active === false ? (
                  <span className="text-slate-500">inactive</span>
                ) : (
                  <span className="text-emerald-700">active</span>
                )}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            {canManage && driver && (
              <button
                type="button"
                onClick={() => onEdit(driver)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-slate-700 bg-surface border border-slate-200 rounded-lg hover:bg-slate-50"
              >
                <FaEdit size={11} /> Edit
              </button>
            )}
            <button
              onClick={onClose}
              aria-label="Close"
              className="p-2 text-slate-400 hover:text-slate-600"
            >
              <FaTimes size={16} />
            </button>
          </div>
        </div>

        {error && (
          <p
            role="alert"
            className="mx-6 mt-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2"
          >
            {error}
          </p>
        )}
        {loading && !data && <LoadingState label="Loading the driver..." />}

        {driver && (
          <>
            <Section title="Reach">
              <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                <dt className="text-slate-500">Mobile</dt>
                <dd className="text-slate-900 tabular-nums">{driver.phone_e164 || '—'}</dd>
                <dt className="text-slate-500">Telegram</dt>
                <dd className="text-slate-900">
                  {driver.telegram_chat_id ? 'linked' : 'not linked'}
                </dd>
                <dt className="text-slate-500">Preferred channel</dt>
                <dd className="text-slate-900">
                  {driver.preferred_channel
                    ? channelLabel(driver.preferred_channel)
                    : 'tenant default'}
                </dd>
              </dl>
              <p className={`mt-2 text-sm ${reach.length ? 'text-slate-700' : 'text-amber-800'}`}>
                {reach.length
                  ? `Reachable on ${reach.map(channelLabel).join(', ')}.`
                  : 'Not reachable on any channel yet — add a number and record an opt-in, or link Telegram.'}
              </p>
              {driver.notes && (
                <p className="mt-2 text-sm text-slate-600 whitespace-pre-wrap">{driver.notes}</p>
              )}
            </Section>

            <Section
              title="Consent"
              action={
                canManage && !consentOpen ? (
                  <button
                    type="button"
                    onClick={() => setConsentOpen(true)}
                    className="inline-flex items-center gap-1 text-xs font-semibold text-primary-700 hover:underline"
                  >
                    <FaPlus size={9} /> Record consent
                  </button>
                ) : null
              }
            >
              <table className="w-full text-sm">
                <caption className="sr-only">Current consent per channel</caption>
                <thead>
                  <tr className="text-left text-xs text-slate-500">
                    <th className="font-medium py-1">Channel</th>
                    <th className="font-medium py-1">State</th>
                    <th className="font-medium py-1">How</th>
                    <th className="font-medium py-1">When</th>
                  </tr>
                </thead>
                <tbody>
                  {DRIVER_CHANNELS.map((c) => {
                    const row = currentMap[c.value];
                    return (
                      <tr key={c.value} className="border-t border-slate-100">
                        <td className="py-1.5">
                          <ChannelBadge channel={c.value} />
                        </td>
                        <td className="py-1.5">
                          <ConsentChip state={row?.state} />
                        </td>
                        <td className="py-1.5 text-slate-700">{row?.method || '—'}</td>
                        <td className="py-1.5 text-slate-700 tabular-nums">
                          {row?.at ? fmtDateTime(row.at) : '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {history.length > 0 && (
                <button
                  type="button"
                  onClick={() => setHistoryOpen((o) => !o)}
                  aria-expanded={historyOpen}
                  className="mt-2 text-xs font-medium text-primary-700 hover:underline"
                >
                  {historyOpen ? 'Hide' : 'Show'} consent history ({history.length})
                </button>
              )}
              {historyOpen && (
                <ul className="mt-2 space-y-2" aria-label="Consent history">
                  {history.map((h) => (
                    <li
                      key={h.id}
                      className="text-xs text-slate-700 border-l-2 border-slate-200 pl-3"
                    >
                      <span className="font-semibold">{channelLabel(h.channel)}</span> ·{' '}
                      {CONSENT_META[h.state]?.label || h.state} · {h.method} · {fmtDateTime(h.at)}
                      {h.recorded_by ? ` · by ${h.recorded_by}` : ''}
                      {h.wording && (
                        <span className="block text-slate-500 italic">“{h.wording}”</span>
                      )}
                      {h.evidence && Object.keys(h.evidence).length > 0 && (
                        <span className="block text-slate-500">
                          Evidence:{' '}
                          {Object.entries(h.evidence)
                            .map(
                              ([k, v]) =>
                                `${k.replace(/_/g, ' ')} ${typeof v === 'object' ? JSON.stringify(v) : v}`
                            )
                            .join(' · ')}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              )}
              {consentOpen && (
                <ConsentForm
                  driver={driver}
                  onCancel={() => setConsentOpen(false)}
                  onSaved={() => {
                    setConsentOpen(false);
                    refresh();
                  }}
                />
              )}
            </Section>

            <Section
              title="Allocations"
              action={
                canManage && !allocOpen && driver.active !== false ? (
                  <button
                    type="button"
                    onClick={() => setAllocOpen(true)}
                    className="inline-flex items-center gap-1 text-xs font-semibold text-primary-700 hover:underline"
                  >
                    <FaPlus size={9} /> Allocate a truck
                  </button>
                ) : null
              }
            >
              <AllocationTimeline allocations={allocations} now={now} />
              {allocOpen && (
                <div className="mt-3">
                  <AllocationEditor
                    driverId={driver.id}
                    assets={assets}
                    onCancel={() => setAllocOpen(false)}
                    onSaved={() => {
                      setAllocOpen(false);
                      refresh();
                    }}
                  />
                </div>
              )}
              {allocations.length === 0 ? (
                <p className="mt-3 text-sm text-slate-500">No allocations yet.</p>
              ) : (
                <ul className="mt-3 divide-y divide-slate-100" aria-label="Allocation list">
                  {allocations.map((a) => {
                    const active = isCurrent(a, now);
                    const future = spanOf(a).fromMs > now;
                    return (
                      <li
                        key={a.id}
                        className="py-2 flex items-center justify-between gap-3 text-sm"
                      >
                        <span className="min-w-0">
                          <span className="font-mono font-semibold text-slate-900">
                            {a.registration || 'Truck'}
                          </span>
                          {a.cancelled && (
                            <span className="ml-2 text-xs text-slate-500">(cancelled)</span>
                          )}
                          {active && (
                            <span className="ml-2 text-xs font-semibold text-emerald-700">now</span>
                          )}
                          <span className="block text-xs text-slate-500 tabular-nums">
                            {fmtDateTime(a.from)} → {a.to ? fmtDateTime(a.to) : 'open-ended'} ·{' '}
                            {a.source}
                          </span>
                        </span>
                        {canManage && !a.cancelled && (active || future) && (
                          <button
                            type="button"
                            onClick={() => endAllocation(a)}
                            disabled={busy === a.id}
                            className="text-xs font-medium text-slate-700 border border-slate-200 rounded-md px-2 py-1 hover:bg-slate-50 disabled:opacity-50"
                          >
                            {future ? 'Cancel' : 'End now'}
                          </button>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </Section>

            <Section
              title="Messages"
              action={
                <Link
                  to={`/lexistar/messages?driver_id=${driver.id}`}
                  className="inline-flex items-center gap-1 text-xs font-semibold text-primary-700 hover:underline"
                >
                  <FaComments size={10} /> Conversation
                </Link>
              }
            >
              {messages.length === 0 ? (
                <p className="text-sm text-slate-500">No messages to or from this driver yet.</p>
              ) : (
                <ul className="divide-y divide-slate-100" aria-label="Recent messages">
                  {messages.map((m) => (
                    <li key={m.id} className="py-2 text-sm">
                      <span className="flex items-center gap-2 flex-wrap">
                        <span
                          aria-label={m.direction === 'in' ? 'From the driver' : 'To the driver'}
                          className="text-slate-500"
                        >
                          {m.direction === 'in' ? '↙' : '↗'}
                        </span>
                        <ChannelBadge channel={m.channel} />
                        <span className="text-slate-700">{intentLabel(m)}</span>
                        <StateChip state={m.state} />
                        <span className="ml-auto text-xs text-slate-500 tabular-nums">
                          {fmtDateTime(m.created_at)}
                        </span>
                      </span>
                      {m.body && (
                        <span className="block mt-1 text-slate-600 line-clamp-2">{m.body}</span>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </Section>
          </>
        )}
      </aside>
    </div>
  );
}
